<template lang="pug">
    .form__body
        .form__field.form__field--inline
            label.form__label.form__title
                | {{ $i18n.t('olympiads.olympiads_olympiadForm_group') }}
                | &nbsp;
                | {{ level.position + 1 }}
            .form__field-body
                a.remove-button(
                    href="javascript://"
                    @click="$emit('removeLevel', index)"
                )
                    svg-icon(name="circle-close", width="2em", height="2em")
        .form__field
            label.form__label
                | {{ $i18n.t('olympiads.olympiads_olympiadForm_course') }}
            .form__field-body
                cs-multiselect(
                    :options="courses",
                    @input="onCourseChangeHandler(level, $event)",
                    :value="level.courses",
                    :placeholder="$i18n.t('common.common_placeholder_enterToSearch')",
                    @open="$emit('getCourses')",
                    :multiple="true",
                    :allowEmpty="true",
                    :loading="loadingCourses",
                    label="name",
                    trackBy="id"
                )
        .form__field
            label.form__label
                | {{ $i18n.tc('olympiads.olympiads_olympiadForm_schools', 1) }}
            .form__field-body
                cs-multiselect(
                    :options="levelSchools",
                    :value="level.schools",
                    :placeholder="schoolsPlaceholder",
                    label="name",
                    trackBy="id",
                    :loading="loadingSchools",
                    @open="$emit('getSchools')",
                    @input="onSchoolChangeHandler(level, $event)",
                    @close="$emit('onSchoolCloseHandler')",
                    :hideSelected="true",
                    :showNoResults="false",
                    :disabled="!level.courses.length",
                    :closeOnSelect="false",
                )
        .form__field
            label.form__label
                | {{ $i18n.tc('olympiads.olympiads_olympiadForm_grades', 1) }}
            .form__field-body
                cs-multiselect(
                    :options="levelGrades",
                    :value="level.grades",
                    :placeholder="gradesPlaceholder",
                    label="name",
                    trackBy="id",
                    :loading="loadingGrades",
                    @open="$emit('getGrades')",
                    @input="onGradeChangeHandler(level, $event)",
                    @close="$emit('onGradeCloseHandler')"
                    :hideSelected="true",
                    :showNoResults="false",
                    :disabled="!(level.schools && level.schools.length)",
                    :closeOnSelect="false"
                )
        .form__field
            label.form__label
                | {{ $i18n.t('olympiads.olympiads_olympiadForm_students') }}
            .form__field-body
                cs-multiselect(
                    :options="levelStudents",
                    v-model="level.students",
                    :placeholder="studentsPlaceholder",
                    label="fullName",
                    trackBy="id",
                    :loading="loadingStudents",
                    @open="$emit('getStudents')",
                    :hideSelected="true",
                    :showNoResults="false",
                    :disabled="!(level.grades && level.grades.length)",
                    :closeOnSelect="false"
                )
        .form__field
            label.form__label
                | {{ $i18n.t('olympiads.olympiads_olympiadForm_theme') }}
            .form__field-body
                cs-input.form__input(
                    v-model="level.theme",
                    v-validate="'required'",
                    :data-vv-as="$i18n.t('olympiads.olympiads_olympiadForm_theme')",
                    :name="'theme' + index"
                )
                help-text(error, slot="help-text", v-if="errors.has('theme' + index)")
                    | {{ errors.first('theme' + index) }}
        .form__field(
            v-if="!level.isRealTime"
            :class="{'form__field--small-stack-space': errors.has('duration' + index)}"
        )
            label.form__label
                | {{ $i18n.t('olympiads.olympiads_olympiadForm_length') }}
            .form__field-body.form__field-body--inline
                cs-input.form__input.input-number--small(
                    v-model.number="level.duration",
                    type="number",
                    v-validate="'required|min_value:1'",
                    :data-vv-as="$i18n.t('olympiads.olympiads_olympiadForm_length')",
                    :name="'duration' + index"
                )
                .field__text {{ $i18n.tc('olympiads.olympiads_timeDeclination_minutes', level.duration) }}
        .form__field(v-if="!level.isRealTime && errors.has('duration' + index)")
            label.form__label
            .form__field-body.form__field-body--inline
                help-text(error)
                    | {{ errors.first('duration' + index) }}
        .form__field.form__field--small-stack-space
            label.form__label
                | {{ $i18n.t('olympiads.olympiads_olympiadForm_timeForAnswer') }}
            .form__field-body.form__field-body--inline
                cs-input.form__input.input-number--small(
                    v-model.number="level.answerDuration",
                    type="number",
                    v-validate="'min_value:1'",
                    :data-vv-as="$i18n.t('olympiads.olympiads_olympiadForm_timeForAnswer')",
                    :name="'answerDuration' + index"
                )
                .field__text {{ $i18n.tc('olympiads.olympiads_timeDeclination_seconds', level.answerDuration) }}
        .form__field
            label.form__label
            .form__field-body
                help-text(:error="errors.has('answerDuration' + index)")
                    | {{ getAnswerDurationHelper(index) }}
        .form__field(:class="{'form__field--small-stack-space': errors.has('startTime' + index) || errors.has('endTime' + index)}")
            label.form__label
                | {{ $i18n.t('olympiads.olympiads_olympiadForm_startTime') }}
            .form__field-body.form__field-body--inline
                .date-selector
                    cs-input.form__input(
                        v-model="level.startTime",
                        v-validate="`required|date_format:HH:mm${startTimeAfter}`",
                        :data-vv-as="$i18n.t('olympiads.olympiads_olympiadForm_startTime')",
                        :name="'startTime' + index"
                    )
                div.date-divider &mdash;
                .date-selector
                    cs-input.form__input(
                        v-model="level.endTime",
                        v-validate="endTimeValidator(index)",
                        :data-vv-as="$i18n.t('olympiads.olympiads_olympiadForm_endTime')",
                        :name="'endTime' + index"
                        :disabled="level.isRealTime"
                    )
        .form__field(v-if="errors.has('startTime' + index) || errors.has('endTime' + index)")
            label.form__label
            .form__field-body
                help-text(error)
                    | {{ errors.first('startTime' + index) || errors.first('endTime' + index) }}
        .form__field
            label.form__label
            .form__field-body
                cs-checkbox(v-model="level.isRealTime") {{ $i18n.t('olympiads.olympiads_olympiadForm_sameTime') }}
        .form__field
            label.form__label
            .form__field-body
                cs-checkbox(v-model="level.isTrainerAvailable")
                    | {{ $i18n.t('olympiads.olympiads_olympiadForm_trainerIsAvailable') }}
        .form__field(v-if="level.isTrainerAvailable")
            label.form__label
                | {{ $i18n.t('olympiads.olympiads_olympiadForm_stopTrainerDate') }}
            .form__field-body
                cs-datepicker.form__input--small(
                    :language="user.locale",
                    :value="level.stopTrainerDate",
                    :format="customFormatter",
                    @input="$emit('stopTrainerDate', {'levelId': index, 'event': $event})",
                    v-validate="trainerValidator",
                    :data-vv-as="$i18n.t('olympiads.olympiads_olympiadForm_stopTrainerDate')",
                    :name="'stopTrainerDate' + index"
                )
                help-text(v-if="errors.has('stopTrainerDate' + index)", error)
                    | {{ errors.first('stopTrainerDate' + index) }}
        divider
        .form__buttons
            cs-button.form__button(type="button" color="blue" pattern="primary-outline" @click="$emit('editLevel', index)", narrow)
                svg-icon(name="cog" size="large" slot="icon")
                | {{ $i18n.t('olympiads.olympiads_olympiadForm_set') }}
        .form__field
            slot(name="settings")
</template>

<script>
    import _ from 'lodash';
    import stageTemplate from './config/stageTemplate.json';
    import { mapState } from 'vuex';
    import moment from 'moment';

    export default {
        name: 'LevelForm',
        props: {
            level: {
                type: Object,
                default: () => {},
            },
            index: {
                type: Number,
                default: 0,
            },
            courses: {
                type: Array,
                default: () => [],
            },
            schools: {
                type: Array,
                default() {
                    return [];
                },
            },
            grades: {
                type: Array,
                default() {
                    return [];
                },
            },
            students: {
                type: Array,
                default() {
                    return [];
                },
            },
            loadingCourses: {
                type: Boolean,
                default: false,
            },
            loadingSchools: {
                type: Boolean,
                default: false,
            },
            loadingGrades: {
                type: Boolean,
                default: false,
            },
            loadingStudents: {
                type: Boolean,
                default: false,
            },
            coursesPlaceholder: {
                type: String,
                default: '',
            },
            schoolsPlaceholder: {
                type: String,
                default: '',
            },
            gradesPlaceholder: {
                type: String,
                default: '',
            },
            studentsPlaceholder: {
                type: String,
                default: '',
            },
            startTimeAfter: {
                type: String,
                default: '',
            },
            trainerValidator: {
                type: String,
                default: 'required',
            },
        },
        computed: {
            ...mapState({
                user: state => state.auth.user,
            }),
            levelSchools() {
                let schools = [];

                if (this.level?.courses?.length) {
                    this.grades.forEach(grade => {
                        const isGradeAvailable =
                            this.level.courses.some(course => grade.courseId === course.id) &&
                            !schools.some(school => +school.id === +grade.school.id);

                        if (isGradeAvailable) {
                            schools.push(grade.school);
                        }
                    });
                }

                return schools;
            },
            levelGrades() {
                return this.level.schools.length
                    ? this.grades.filter(grade => {
                          return this.level.schools.some(school => school.id === grade.schoolId);
                      })
                    : [];
            },
            levelStudents() {
                return this.level.grades.length
                    ? this.students.filter(student => {
                          return this.level.grades.some(grade => {
                              return student.grades.map(grade => grade.id).includes(grade.id);
                          });
                      })
                    : [];
            },
        },
        watch: {
            'level.isRealTime'(val) {
                val && (this.level.endTime = null);
            },
        },
        methods: {
            onCourseChangeHandler(level, selected) {
                this.$set(
                    level,
                    'schools',
                    level.schools.filter(school =>
                        selected.some(course => school.courses.map(course => course.id).includes(course.id))
                    )
                );
                this.$set(
                    level,
                    'grades',
                    level.grades.filter(grade => level.schools.some(school => school.id === grade.school.id))
                );
                this.$set(
                    level,
                    'students',
                    level.students.filter(student =>
                        level.grades.some(grade => student.grades.some(studentGrade => studentGrade.id === grade.id))
                    )
                );
                level.courses = selected;
                level.stages = [_.cloneDeep(stageTemplate)];
            },
            onSchoolChangeHandler(level, selected) {
                this.$set(
                    level,
                    'grades',
                    level.grades.filter(grade => selected.some(school => school.id === grade.school.id))
                );
                this.$set(
                    level,
                    'students',
                    level.students.filter(student =>
                        level.grades.some(grade => student.grades.some(studentGrade => studentGrade.id === grade.id))
                    )
                );
                level.schools = selected;
            },
            onGradeChangeHandler(level, selected) {
                this.$set(
                    level,
                    'students',
                    level.students.filter(student =>
                        selected.some(grade => student.grades.some(studentGrade => studentGrade.id === grade.id))
                    )
                );
                level.grades = selected;
            },
            getAnswerDurationHelper(index) {
                return this.errors.has('answerDuration' + index)
                    ? this.errors.first('answerDuration' + index)
                    : this.$i18n.t('olympiads.olympiads_olympiadForm_leaveTimeEmpty');
            },
            endTimeValidator(index) {
                let validator = this.level.isRealTime ? 'required|' : '';
                return this.errors.has('startTime' + index)
                    ? validator + 'date_format:HH:mm'
                    : validator + 'date_format:HH:mm|after:' + this.level.startTime;
            },
            customFormatter(date, format = 'D MMM YYYY') {
                return moment(date).format(format);
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import '@/assets/sass/forms/form.scss';
    @import '@/assets/sass/forms/dates.scss';

    .input-number--small {
        max-width: 160px;
        margin-right: 16px;
    }

    .remove-button {
        display: flex;
        justify-content: flex-end;
        color: $hsl-color-green-48;
        border-radius: $border-radius-circle;
        &:hover {
            color: $hsl-color-red-62;
            background-color: transparent;
        }
    }
</style>
